import React, { useState, useEffect, FunctionComponent, useRef } from "react";
import Select from "react-select";
import Creatable from "react-select/creatable";
import DropdownIndicator from "src/components/general/DropdownIndicator";
import { ReactSelectOption } from "src/lib/globalTypes";
import { VariableSizeList as List, VariableSizeList } from "react-window";

/**
 * This is a wrapper for the dropdown provided by the react-select library.
 *
 * It's here to avoid having repeating common things all our Selects need:
 *  - classnames
 *  - use custom DropdownIndicator
 *  - use custom handleSearchInput function for sorting search results
 *
 * ...and adding a hidden required input if needed to make the field required.
 * react-select does not have a 'required' feature as of Jan 2020.
 *
 * For documentation on props please refer to https://github.com/JedWatson/react-select
 */

type DropdownProps = {
  className?: string;
  options: Array<ReactSelectOption & { subLabel?: string }>;
  value: ReactSelectOption | ReactSelectOption[] | null | undefined;
  onChange: (option: any) => void;
  onBlur?: () => void;
  isSearchable?: boolean;
  isClearable?: boolean;
  isMulti?: boolean;
  isOptionDisabled?: (option: ReactSelectOption) => boolean;
  noOptionsMessage?: string;
  creatable?: boolean;
  placeholder?: string;
  id?: string;
  dark?: boolean;
  required?: boolean;
  isDisabled?: boolean;

  // large lists will use the MenuList component for improved menu performance.
  // it comes with some styling costs which is why it is not enabled by default.
  // e.g. no options shows a large blank list rather than 'No options' message
  largeList?: boolean;

  // Override the filter behavior on searchable menus
  handleSearchInput?: (
    inputValue: string,
    { action }: { action: "menu-close" | "input-change" }
  ) => void;

  // See https://react-select.com/components
  // for full list and docs on customisable components
  components?: {
    Option?: (props: any) => JSX.Element;
    SingleValue?: (props: any) => JSX.Element;
    MultiValue?: (props: any) => JSX.Element;
  };
};

const MenuList = (props: any) => {
  const { options, children } = props;

  const listRef = useRef<VariableSizeList>(null);

  // by default react-window caches the size of children at specific indices
  // when filtering, our children will move places, so we want to reset this
  React.useEffect(() => {
    if (listRef?.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [children, children.length]);

  const calculateSize = (index: number): number => {
    const subLabel = options[index]?.subLabel;

    if (!subLabel || subLabel.length === 0) {
      return 36;
    } else if (subLabel.length < 55) {
      return 46;
    } else {
      return 60;
    }
  };

  return (
    <List
      ref={listRef}
      height={200}
      estimatedItemSize={46}
      itemCount={children.length}
      itemSize={calculateSize}
      width={"100%"}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

const Dropdown: FunctionComponent<DropdownProps> = ({
  className,
  options,
  value,
  onChange,
  onBlur,
  isSearchable = true,
  isClearable = false,
  isMulti,
  isOptionDisabled,
  noOptionsMessage,
  creatable,
  placeholder,
  id,
  dark,
  required,
  isDisabled,
  handleSearchInput,
  components,
  largeList,
}) => {
  const [liveOptions, setOptions] = useState(
    options?.filter((o) => !o.nonCore)
  );

  useEffect(() => {
    setOptions(options?.filter((o) => !o.nonCore));
  }, [options]);

  if (!handleSearchInput) {
    // this will prioritise matches which begin with the search string.
    // the default behaviour we are overriding is to sort alphabetically.
    handleSearchInput = (inputValue: string, { action }: any) => {
      if (action === "input-change") {
        let foundOptions = options.filter((option) => {
          if (typeof option.label !== "string") {
            return false;
          }
          return (
            (option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.subLabel
                ?.toLowerCase()
                .includes(inputValue.toLowerCase())) &&
            (!option.nonCore || inputValue.length > 2)
          );
        });
        if (inputValue !== "") {
          foundOptions = foundOptions.sort((a, b) => {
            if (!(typeof a.label === "string" && typeof b.label === "string")) {
              return 0;
            }
            if (a.label.toLowerCase() === inputValue.toLowerCase()) {
              return -1;
            } else if (b.label.toLowerCase() === inputValue.toLowerCase()) {
              return 1;
            } else if (
              a.label.toLowerCase().startsWith(inputValue.toLowerCase())
            ) {
              return -1;
            } else if (
              b.label.toLowerCase().startsWith(inputValue.toLowerCase())
            ) {
              return 1;
            } else {
              return a.label.localeCompare(b.label);
            }
          });
        }
        setOptions(foundOptions);
      } else if (action === "menu-close") {
        setOptions(options);
      }
    };
  }

  const customComponents: any = {
    DropdownIndicator,
    ...components,
  };
  if (largeList) {
    customComponents.MenuList = MenuList;
  }

  const props: any = {
    options: liveOptions,
    value,
    onChange: (option: ReactSelectOption) => {
      onChange(option);
      setOptions(options);
    },
    onBlur,
    components: customComponents,
    isMulti,
    isClearable,
    isSearchable,
    isOptionDisabled,
    noOptionsMessage: () => noOptionsMessage,
    placeholder,
    classNamePrefix: "react-select",
    className: "react-select-container" + (dark ? " dark" : ""),
    inputId: id,
    isDisabled,
  };

  // only bother to override filtering behaviour if we are allowing filtering
  if (isSearchable) {
    props.onInputChange = handleSearchInput;
    props.filterOption = () => true;
  }

  return (
    <div className={"dropdown-wrapper" + (className ? ` ${className}` : "")}>
      {creatable ? <Creatable {...props} /> : <Select {...props} />}
      {required && (
        <input
          tabIndex={-1}
          value={props.value || ""}
          onChange={() => null}
          autoComplete="off"
          className="required-input-enforcement"
          required
        />
      )}
      <style jsx>{`
        .dropdown-wrapper {
          width: 100%;
          position: relative;
          cursor: ${isDisabled ? "not-allowed" : "default"};
        }

        .required-input-enforcement {
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          position: absolute;
          top: 42px;
          left: 82px;
        }
      `}</style>
    </div>
  );
};

export default Dropdown;
